import { useTheme } from '@emotion/react';
import { faEye as eye } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowDown,
  faArrowUp,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { lazy } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  FormattedTime,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { redirect, useNavigate } from 'react-router';

import { getFundsCurrentTab } from '!views/Market/Funds/redux';
import {
  TrlCheckButton,
  TrlFavoriteIcon,
  TrlFlag,
  TrlPriceType,
  TrlTextPill,
} from '»components/atoms';
import { TrlOperationType } from '»components/atoms/TrlOperationType';
import { iff } from '»utils/code';
import {
  useCurrentPage,
  useIsOrderCancellable,
  useIsOrderEditable,
  useIsTitleNegotiable,
  useNegModalContext,
  useOrdStatusLabel,
  useTrancheTypeLabel,
} from '»utils/customHooks';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { getEndDecorator as getPriceRepreEndDecorator } from '»utils/endDecorator';
import { getFundType } from '»utils/funds';
import { TrlFormattedNumberPlain } from '»utils/i18n';
import { displayNominalValue } from '»utils/nominalValue';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';
import { getPctgColor } from '»utils/pctgColors';
import {
  getOperationLabel,
  getOrderTypeLabel,
} from '»utils/trading/trading.utils';
import { TrlFieldDefaultValue } from '»utils/trlFieldDefaultValue';

import {
  CustomTableCell,
  EyeIcon,
  FontAwesomeIconStyled,
  UppercaseCell,
  UppercaseLabel,
  ValueZeroStyled,
  VariationStyle,
} from './TrlButtonCell.styles';

const TrlGridCellTrlPillAmount = lazy(
  () =>
    import(
      /* webpackChunkName: "TrlPillAmount" */ '»components/atoms/TrlPills/TrlPillAmount'
    ),
);

const TrlGridCellTrlPillQuantity = lazy(
  () =>
    import(
      /* webpackChunkName: "TrlPillQuantity" */ '»components/atoms/TrlPills/TrlPillQuantity'
    ),
);

const ButtonPlus = lazy(
  () =>
    import(
      /* webpackChunkName: "ButtonPlus" */ '»components/atoms/Buttons/ButtonPlus'
    ),
);

const ButtonMinus = lazy(
  () =>
    import(
      /* webpackChunkName: "ButtonMinus" */ '»components/atoms/Buttons/ButtonMinus'
    ),
);

const TrlManageOrderButton = lazy(
  () =>
    import(
      /* webpackChunkName: "TrlManageOrderButton" */ '»components/molecules/TrlManageOrderButton'
    ),
);

const ButtonFile = lazy(
  () =>
    import(
      /* webpackChunkName: "ButtonFile" */ '»components/atoms/Buttons/ButtonFile'
    ),
);

const TrlRadioButton = lazy(
  () =>
    import(
      /* webpackChunkName: "TrlRadioButton" */ '»components/atoms/Inputs/TrlRadioButton'
    ),
);

export const TrlButtonCell = (props) => {
  const { column, field, dataItem } = props;
  const value = dataItem[field];
  const {
    type,
    visualEffect,
    hasVisualEffectAttr,
    decimalDigits,
    isMarketStockExchange,
    endDecorator,
    endDecoratorKey,
    fieldValue,
    disabledFieldValue,
    disableCondition,
    setFavourite,
  } = column;
  const navigate = useNavigate();
  const theme = useTheme();

  const fundsActiveTab = useSelector(getFundsCurrentTab);

  const { openInsertOrderModal } = useNegModalContext();
  const { getOrdStatusLabel } = useOrdStatusLabel();
  const { getTrancheTypeLabel } = useTrancheTypeLabel();
  const { isMarketPrimaryPage, isFundsListPage } = useCurrentPage();

  const isFundsPrimaryListPage =
    isFundsListPage && fundsActiveTab.name === 'fundsPrimary';

  const isPrimaryScreen = isMarketPrimaryPage || isFundsPrimaryListPage;

  const isMarketFundManager = getFundType(dataItem) === 'fundsManager';

  const isPlusButtonDisabled = !useIsTitleNegotiable({
    isMarketPrimaryScreen: isMarketPrimaryPage,
    isFundsPrimaryScreen: isFundsPrimaryListPage,
    canTrade: dataItem?.canTrade,
    endDate: dataItem?.endDate,
    startDate: dataItem?.startDate,
  });

  const isMinusButtonDisabled = !useIsTitleNegotiable({
    isMarketPrimaryScreen: false,
    isFundsPrimaryScreen: false,
    canTrade: dataItem?.canTrade,
  });

  const isEditButtonDisabled = !useIsOrderEditable({
    revokeDate: dataItem.revokeDate,
    ordStatus: dataItem.ordStatus,
    securityType: dataItem.securityType,
    side: dataItem.side,
    localStartTime: dataItem?.localStarTtime,
    localEndTime: dataItem?.localEndTime,
  });

  const isCancelButtonDisabled = !useIsOrderCancellable({
    revokeDate: dataItem.revokeDate,
    ordStatus: dataItem.ordStatus,
  });

  let disabled;

  const getEndDecorator = () => {
    if (endDecorator) return endDecorator;

    if (endDecoratorKey && endDecoratorKey === 'priceRepresentation')
      return getPriceRepreEndDecorator(
        dataItem[endDecoratorKey],
        field,
        dataItem.currency,
      );

    if (endDecoratorKey && endDecoratorKey !== 'priceRepresentation')
      return ` ${dataItem[endDecoratorKey]}`;

    return null;
  };

  switch (type !== undefined ? type.toUpperCase() : 'MINUS') {
    case 'PLUS': {
      return (
        <CustomTableCell
          {...props}
          style={{
            padding: '0px',
            width: '40px',
          }}
        >
          <ButtonPlus
            disabled={isPlusButtonDisabled}
            isGrid
            onClick={() => {
              if (isPrimaryScreen) {
                navigate(column.path, { state: { dataItem } });
              } else {
                openInsertOrderModal(isMarketFundManager ? 'D' : 1);
              }
            }}
          />
        </CustomTableCell>
      );
    }
    case 'STATE':
      return (
        <CustomTableCell {...props} title={dataItem[field]}>
          {value.toUpperCase() === '1' && <TrlGridCellTrlPillAmount />}
          {value.toUpperCase() !== '1' && <TrlGridCellTrlPillQuantity />}
        </CustomTableCell>
      );

    case 'STATUS': {
      const OrdStatusElement = getOrdStatusLabel(value, 'pill');

      return (
        <CustomTableCell {...props}>
          <OrdStatusElement />
        </CustomTableCell>
      );
    }
    case 'TEXTPILL':
      return (
        <CustomTableCell {...props}>
          <TrlTextPill value={value} />
        </CustomTableCell>
      );
    case 'EDIT': {
      return (
        <CustomTableCell
          {...props}
          style={{
            padding: '0px',
            width: '40px',
          }}
        >
          <TrlManageOrderButton
            variant="edit"
            dataItem={dataItem}
            disabled={isEditButtonDisabled}
          />
        </CustomTableCell>
      );
    }
    case 'CLOSE': {
      return (
        <CustomTableCell
          {...props}
          style={{
            padding: '0px',
            width: '40px',
          }}
        >
          <TrlManageOrderButton
            variant="cancel"
            dataItem={dataItem}
            disabled={isCancelButtonDisabled}
          />
        </CustomTableCell>
      );
    }
    case 'OPENFILESMODAL':
      return (
        <CustomTableCell
          {...props}
          style={{
            padding: '0px',
            width: '40px',
          }}
        >
          <ButtonFile
            isGrid
            disabled={!value && !column.handleOpenFilesModal}
            onClick={column.handleOpenFilesModal}
          />
        </CustomTableCell>
      );
    case 'FAVORITE': {
      const isDisabled = !dataItem.instrument;

      return (
        <CustomTableCell {...props}>
          <TrlFavoriteIcon
            isDisabled={isDisabled}
            isFavorite={dataItem[field] === 'Y'}
            onClick={() => {
              setFavourite({ dataItem });
            }}
          />
        </CustomTableCell>
      );
    }
    case 'DATE':
      return (
        <CustomTableCell {...props}>
          <TrlDateFormatted date={dataItem[field]} />
        </CustomTableCell>
      );
    case 'DATETIME':
      return (
        <CustomTableCell {...props}>
          <FormattedDate value={dataItem[field]} />
          {' | '}
          <FormattedTime value={dataItem[field]} />
        </CustomTableCell>
      );
    case 'DATETIMESEC': {
      const cellVal = dataItem[field];
      return (
        <CustomTableCell {...props}>
          {cellVal && (
            <>
              <TrlDateFormatted date={dataItem[field]} />
              {' | '}
              <TrlTimeFormatted
                value={dataItem[field]}
                hour="numeric"
                minute="numeric"
                second="numeric"
              />
            </>
          )}
          {!cellVal && <TrlFieldDefaultValue />}
        </CustomTableCell>
      );
    }
    case 'TIME':
      return (
        <CustomTableCell {...props}>
          <FormattedTime value={dataItem[field]} />
        </CustomTableCell>
      );

    case 'TIMESEC':
      return (
        <CustomTableCell {...props}>
          <FormattedTime
            value={dataItem[field]}
            hour="numeric"
            minute="numeric"
            second="numeric"
          />
        </CustomTableCell>
      );
    case 'NUMERIC': {
      const valueDecimalDigits =
        decimalDigits === 'roundPrice' || decimalDigits === 'quantityPrecision'
          ? getDecimalDigits(dataItem[decimalDigits])
          : decimalDigits;

      if (field === 'nominalValue') {
        const nominalValue = displayNominalValue(
          dataItem.quantityRepresentation,
          dataItem.priceRepresentation,
          dataItem[field],
        );

        return (
          <CustomTableCell
            {...props}
            title={
              nominalValue
                ? TrlFormattedNumberPlain(nominalValue, valueDecimalDigits)
                : undefined
            }
          >
            <TrlNumberFormatted
              value={nominalValue}
              decimalDigits={valueDecimalDigits}
            />
          </CustomTableCell>
        );
      }

      if (visualEffect !== undefined) {
        if (
          !isMarketStockExchange ||
          (isMarketStockExchange && dataItem[visualEffect] !== null)
        ) {
          const title =
            isMarketStockExchange && dataItem[visualEffect] === 0
              ? `${TrlFormattedNumberPlain(0, valueDecimalDigits)}%`
              : !!dataItem[field] &&
                `${TrlFormattedNumberPlain(
                  dataItem[field],
                  valueDecimalDigits,
                )}${getEndDecorator() || ''}`;

          return (
            <CustomTableCell
              {...props}
              style={{
                color: getPctgColor(parseFloat(dataItem[visualEffect]), theme),
              }}
              title={title}
            >
              <VariationStyle
                value={dataItem[field]}
                hasVisualEffectAttr={hasVisualEffectAttr}
              >
                {isMarketStockExchange && dataItem[visualEffect] === 0 ? (
                  <ValueZeroStyled>
                    <FormattedNumber
                      value={0}
                      maximumFractionDigits={valueDecimalDigits}
                      minimumFractionDigits={valueDecimalDigits}
                    />
                    %
                  </ValueZeroStyled>
                ) : (
                  <TrlNumberFormatted
                    value={dataItem[field]}
                    decimalDigits={valueDecimalDigits}
                    endDecorator={getEndDecorator()}
                  />
                )}
                {hasVisualEffectAttr &&
                  (parseFloat(dataItem[field]) > 0 ? (
                    <FontAwesomeIconStyled
                      value={dataItem[field]}
                      icon={faArrowUp}
                    />
                  ) : (
                    iff(
                      parseFloat(dataItem[field]) < 0,
                      <FontAwesomeIconStyled
                        value={dataItem[field]}
                        icon={faArrowDown}
                      />,
                      <FontAwesomeIconStyled
                        value={dataItem[field]}
                        icon={faMinus}
                      />,
                    )
                  ))}
              </VariationStyle>
            </CustomTableCell>
          );
        }
      }

      if (field === 'price' && !dataItem[field]) {
        return (
          <CustomTableCell {...props}>
            <TrlOperationType label="TRLT_UI-MARKET" />
          </CustomTableCell>
        );
      }

      return (
        <CustomTableCell
          {...props}
          title={
            dataItem[field]
              ? `${TrlFormattedNumberPlain(
                  dataItem[field],
                  valueDecimalDigits,
                )}${getEndDecorator() || ''}`
              : undefined
          }
        >
          <TrlNumberFormatted
            value={dataItem[field]}
            decimalDigits={valueDecimalDigits}
            endDecorator={getEndDecorator()}
          />
        </CustomTableCell>
      );
    }

    case 'FLAG':
      return (
        <CustomTableCell {...props}>
          <Box sx={{ display: 'flex', textTransform: 'uppercase' }}>
            <TrlFlag flag={dataItem[field]} />
            {dataItem[column.exchange]}
            {dataItem[column.secondFlag] && (
              <TrlFlag flag={dataItem[column.secondFlag]} />
            )}
          </Box>
        </CustomTableCell>
      );
    case 'TRANCHETYPE': {
      const trancheTypeLabel = getTrancheTypeLabel(dataItem[field]);
      return <CustomTableCell {...props}>{trancheTypeLabel}</CustomTableCell>;
    }
    case 'PRICETYPE':
      return (
        <CustomTableCell {...props}>
          <TrlPriceType value={dataItem[field]} />
        </CustomTableCell>
      );

    case 'EYE':
      return (
        <CustomTableCell {...props}>
          <EyeIcon icon={eye} visible={dataItem[field]} />
        </CustomTableCell>
      );

    case 'RADIO':
      return (
        <CustomTableCell {...props}>
          <TrlRadioButton
            value={dataItem[fieldValue]}
            selectedValue={column.selectedValue}
            onChange={column.handleChange}
          />
        </CustomTableCell>
      );
    case 'CHECKBOX':
      if (disabledFieldValue && disableCondition) {
        disabled = dataItem[disabledFieldValue] === disableCondition;
      }

      return (
        <CustomTableCell {...props}>
          <TrlCheckButton
            id={column}
            value={dataItem[field]}
            handleChange={column.handleChange}
            relatedField={dataItem[fieldValue]}
            disabled={disabled}
          />
        </CustomTableCell>
      );

    case 'OPERATION': {
      const operationLabel = getOperationLabel(dataItem[field]);

      return (
        <CustomTableCell {...props}>
          <TrlOperationType side={dataItem[field]} label={operationLabel} />
        </CustomTableCell>
      );
    }

    case 'ORDERTYPE': {
      const label = getOrderTypeLabel(dataItem[field]);

      return (
        <CustomTableCell {...props}>
          <UppercaseLabel>
            <FormattedMessage id={label} />
          </UppercaseLabel>
        </CustomTableCell>
      );
    }

    case 'UPPERCASECELL': {
      return (
        <UppercaseCell {...props} title={dataItem[field]?.toUpperCase()}>
          {dataItem[field]}
          {column.secondField && ` | ${dataItem[column.secondField]}`}
        </UppercaseCell>
      );
    }
    default:
      return (
        <CustomTableCell
          {...props}
          style={{
            padding: '0px',
            width: '40px',
          }}
        >
          <ButtonMinus
            isGrid
            onClick={() => {
              if (isPrimaryScreen) {
                redirect(column.path, { state: { dataItem } });
              } else {
                openInsertOrderModal(isMarketFundManager ? 'E' : 2);
              }
            }}
            disabled={isMinusButtonDisabled}
          />
        </CustomTableCell>
      );
  }
};

TrlButtonCell.propTypes = {
  column: PropTypes.array,
  field: PropTypes.string,
  value: PropTypes.string,
  dataItem: PropTypes.object,
};
